<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import Input from '@components/global/forms/Input.vue'
import TransitionExpand from '@components/global/transitions/TransitionExpand.vue'

const emit = defineEmits(['update:modelValue'])
const props = defineProps(['modelValue', 'label', 'placeholder', 'options'])
const root = ref(null)
const dropdown = ref(null)
const expanded = ref(false)
const selected = ref([])
const search = ref('')

const selectedOptions = computed(() =>
	props.options.filter(function (option) {
		return selected.value.includes(option.slug)
	}),
)
const filteredUnselectedOptions = computed(() =>
	props.options.filter(function (option) {
		return !selected.value.includes(option.slug)
	}),
)

const filteredOptions = computed(() =>
	props.options.filter(function (option) {
		return option.name.toLowerCase().includes(search.value.toLowerCase())
	}),
)

function toggleExpanded() {
	expanded.value = !expanded.value
}
async function collapse(e) {
	if (!root.value.contains(e.target)) {
		expanded.value = false
	}
}
function toggleSelected(optionSlug) {
	if (selected.value.includes(optionSlug)) {
		selected.value = selected.value.filter(function (s) {
			return s !== optionSlug
		})
	} else {
		selected.value.push(optionSlug)
	}
	emit('update:modelValue', selected.value)
}
function isSelected(optionSlug) {
	return selected.value.includes(optionSlug)
}

watch(
	() => props.modelValue,
	(newValue) => {
		selected.value = newValue ?? []
	},
	{ immediate: true },
)

onMounted(() => {
	selected.value = props.modelValue ?? []
	window.addEventListener('click', collapse)
})
onBeforeUnmount(() => {
	window.removeEventListener('click', collapse)
})
</script>

<template>
	<div class="flex flex-col gap-2">
		<div class="flex justify-between" v-if="label">
			<label class="text-sm text-crowns-off-white" v-if="label">
				{{ label }}
			</label>
			<!-- <span class="label-medium cursor-pointer text-crowns-castle-shadow" @click="global.openModal(['forgot-password'])" v-if="forgot">Forgot password?</span> -->
		</div>
		<div :class="['relative w-full min-w-60 touch-manipulation select-none', expanded ? 'z-30' : 'z-20']" ref="root" role="listbox" aria-multiselectable="true" :aria-expanded="expanded">
			<button type="button" :class="['flex w-full grow items-center justify-between border border-crowns-grey bg-crowns-dark-grey px-2 py-3 text-sm normal-case focus:border-[#2c2c2c] focus:outline-none']" @click="toggleExpanded" tabindex="0">
				<label class="text-sm text-crowns-castle-stone" v-if="placeholder">
					{{ placeholder }}
				</label>
				<i :class="['fa-solid fa-chevron-down text-crowns-castle-stone transition-transform duration-200', { 'rotate-180': expanded }]"></i>
			</button>
			<TransitionExpand>
				<div class="absolute left-0 top-full w-full pt-2" v-if="expanded" ref="dropdown">
					<div class="flex max-h-60 flex-col overflow-y-auto bg-crowns-dark-grey p-1" style="overflow-y: auto">
						<button type="button" v-for="(option, i) in filteredOptions" :key="'unselected' + i" class="group flex touch-manipulation select-none items-center gap-2 p-2 text-sm font-semibold transition-colors duration-200 hover:bg-crowns-grey" role="option" @click="toggleSelected(option.slug)" :aria-selected="isSelected(option.slug)" tabindex="0">
							<div class="flex h-5 w-5 items-center justify-center border-2 border-crowns-grey bg-crowns-off-black">
								<i class="fas fa-check text-crowns-off-white" v-if="isSelected(option.slug)"></i>
							</div>
							<div :class="['font-light text-crowns-clouds transition-colors duration-200 group-hover:text-crowns-off-white', { 'text-crowns-off-white': isSelected(option.slug) }]">{{ option.name }}</div>
						</button>
					</div>
				</div>
			</TransitionExpand>
		</div>
	</div>
</template>
